<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import SuljeLogo from './SuljeLogo.vue';
import { isSignedIn, signIn, signOut } from '@/lib/session';
import { useLocaleSwitch, useMe } from '@/lib/me';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { api } from '@/lib/api';
import { LOCALES } from '@/lib/i18n';

/**
 * Sulje's own chrome. It never takes a project theme (§9): it is how a person
 * knows which platform they are on and which one they are paying.
 */
const { locale } = useI18n();
const me = useMe();
const switchLocale = useLocaleSwitch();

// "Manage membership" opens Stripe's Customer Portal (§4.2) — only for someone
// who has paid through Stripe; a grant has nothing to manage there.
const billing = useQuery({ queryKey: ['billing'], queryFn: api.billing, enabled: isSignedIn });
const portal = useMutation({
  mutationFn: api.portal,
  onSuccess: ({ url }) => location.assign(url),
});

async function leave() {
  await signOut();
  location.assign('/');
}
</script>

<template>
  <header class="border-b border-line bg-surface/90 backdrop-blur">
    <div class="mx-auto flex h-14 max-w-6xl items-center gap-4 px-4 sm:px-8">
      <RouterLink to="/" class="text-ink no-underline">
        <SuljeLogo />
      </RouterLink>
      <div class="flex-1" />
      <div
        role="group"
        :aria-label="$t('nav.language')"
        class="flex rounded-lg border border-line p-0.5 text-xs"
      >
        <button
          v-for="l in LOCALES"
          :key="l"
          type="button"
          class="min-h-8 rounded-md px-2.5 font-semibold uppercase"
          :class="locale === l ? 'bg-ink text-page' : 'text-muted hover:text-ink'"
          :aria-pressed="locale === l"
          @click="switchLocale(l)"
        >
          {{ l }}
        </button>
      </div>
      <template v-if="isSignedIn">
        <span v-if="me.data.value" class="hidden text-sm text-muted sm:inline">
          {{ me.data.value.displayName ?? me.data.value.email }}
        </span>
        <button
          v-if="billing.data.value?.available && billing.data.value.hasCustomer"
          type="button"
          class="hidden min-h-11 rounded-lg px-3 text-sm font-medium text-muted hover:text-ink sm:inline-flex sm:items-center"
          :disabled="portal.isPending.value"
          @click="portal.mutate()"
        >
          {{ $t('nav.membership') }}
        </button>
        <button
          type="button"
          class="min-h-11 rounded-lg px-3 text-sm font-medium text-muted hover:text-ink"
          @click="leave"
        >
          {{ $t('nav.signOut') }}
        </button>
      </template>
      <button
        v-else
        type="button"
        class="min-h-11 rounded-xl bg-sulje px-4 text-sm font-semibold text-sulje-ink"
        @click="signIn()"
      >
        {{ $t('nav.signIn') }}
      </button>
    </div>
  </header>
</template>
